// Copyright (c) StrangeBee 2024

import React, { ReactElement } from 'react';
import { useLocalMessage } from '@Helpers/';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import useValidateForm from '@Hooks/useValidateForm';
import { CustomStatusForm, CustomStatusValuesForm } from './CustomStatusForm';
import { DescribeFieldOutput, CustomStatusInput, CustomStatusMapType, CustomStatusOutput } from 'thehive-sdk';

interface StatusManageProps {
    onFinish: (ObservableType: CustomStatusInput) => void;
    statusList?: CustomStatusMapType;
    triggerElem?: ReactElement;
    describeField?: DescribeFieldOutput;
    data?: CustomStatusOutput;
    visible?: boolean;
    onVisibleChange?: (visible: boolean) => void;
}

export const CustomStatusManage = ({
    onFinish,
    triggerElem,
    describeField,
    data,
    statusList,
    visible,
    onVisibleChange,
}: StatusManageProps): ReactElement => {
    const i = useLocalMessage();

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched } = useValidateForm();

    const onFinishHandler = ({ value = '', stage, colour }: CustomStatusValuesForm): void => {
        onFinish({ value: value.trim(), order: 0, stage: stage.trim(), colour: colour?.trim() });
    };

    return (
        <THDrawer
            title={!data ? i(`custom-status.create.drawer.title`) : i(`custom-status.edit.drawer.title`)}
            triggerElem={triggerElem}
            afterVisibleChange={(visible) => {
                form.resetFields();
                setFieldsTouched(false);
                onVisibleChange?.(visible);
            }}
            visible={visible}
            className="drawer-form"
            disableClose={fieldsTouched}
        >
            {(toggleDrawer) => (
                <CustomStatusForm
                    isValid={isValid && fieldsTouched}
                    describeField={describeField}
                    onFinish={(values) => {
                        onFinishHandler(values);
                        toggleDrawer();
                    }}
                    onCancel={() => {
                        toggleDrawer();
                        form.resetFields();
                        setFieldsTouched(false);
                    }}
                    statusList={statusList}
                    data={data}
                    form={form}
                    onChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                />
            )}
        </THDrawer>
    );
};
